"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/utils/supabase/client";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  // True after the user signs out in this session, so protected pages send
  // them to the landing page rather than the sign-in page.
  signedOut: boolean;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signedOut: false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    const getSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          if (session.user.email_confirmed_at) {
            setUser(session.user);
          } else {
            await supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error("Session check error:", error);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setSignedOut(false);
        if (session.user.email_confirmed_at) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      } else if (event === "SIGNED_OUT") {
        setSignedOut(true);
        setUser(null);
      } else if (event === "TOKEN_REFRESHED" && session?.user) {
        if (session.user.email_confirmed_at) {
          setUser(session.user);
        } else {
          setUser(null);
        }
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signedOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
